// Phase 5 (web revival) — main /library timeline.
//
// useInfiniteQuery against /v1/assets with cursor pagination + a top
// filter bar (All / Favorites / Photos / Videos / Year / Month / Trash).
// The grid itself lives in `components/timeline/TimelineGrid.tsx` — same
// component is reused by /shared-with-me and /album/$id.
//
// `favorite=true` filter: server-side filter isn't shipped yet (the
// CONTRACT doesn't list a `favorite` query param). We approximate it
// client-side by filtering the already-loaded asset pages. This means
// the Favorites view only ever shows favorites among assets the user
// has actually scrolled into. Deferred until the server endpoint
// grows the param.

import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../api/client';
import type { AssetListResponse } from '../api/types';
import { qk } from '../app/queryClient';
import type { AssetsFilter } from '../app/queryClient';
import { setLightboxSource } from '../app/lightboxNav';
import { downloadAssetsZip } from '../lib/download';
import { FilterBar, type LibraryFilter } from '../components/timeline/FilterBar';
import { TimelineGrid } from '../components/timeline/TimelineGrid';
import { Slideshow } from '../components/timeline/Slideshow';

interface ServerFilter {
  include: 'normal' | 'trashed';
  kind?: 'video' | 'photo';
  capture_after?: string;
  capture_before?: string;
}

function toServerFilter(f: LibraryFilter): ServerFilter {
  if (f.mode === 'trash') return { include: 'trashed' };
  if (f.mode === 'video') return { include: 'normal', kind: 'video' };
  // Photos-only. The corpus stores stills as kind='photo' (live_photo/burst
  // aren't used in practice), so an equality filter on the server suffices.
  if (f.mode === 'photos') return { include: 'normal', kind: 'photo' };
  if (f.mode === 'date' && f.year !== undefined && f.month !== undefined) {
    // The month picker always filters a whole calendar month. Date.UTC
    // rolls month 12 over to January of the next year, so the December →
    // next-January boundary needs no special-casing.
    const start = new Date(Date.UTC(f.year, f.month, 1)).toISOString();
    const end = new Date(Date.UTC(f.year, f.month + 1, 1)).toISOString();
    return { include: 'normal', capture_after: start, capture_before: end };
  }
  // All + Favorites both fetch the normal feed; Favorites is filtered
  // client-side below.
  return { include: 'normal' };
}

function toCacheKey(f: LibraryFilter): AssetsFilter {
  const out: AssetsFilter = { include: f.mode === 'trash' ? 'trashed' : 'normal' };
  if (f.mode === 'favorites') out.favorite = true;
  if (f.mode === 'video') out.kind = 'video';
  if (f.mode === 'photos') out.kind = 'photo';
  if (f.mode === 'date' && f.year !== undefined && f.month !== undefined) {
    // Lean on AssetsFilter's structural identity to disambiguate the
    // cache entry — encode the year + month onto the filter object.
    // Safe because the query key compares structurally.
    (out as unknown as { year: number; month: number }).year = f.year;
    (out as unknown as { year: number; month: number }).month = f.month;
  }
  return out;
}

function scrollKeyFor(f: LibraryFilter): string {
  if (f.mode === 'date') return `library:date:${f.year ?? 'none'}-${f.month ?? 'none'}`;
  return `library:${f.mode}`;
}

// Module-level so the chosen filter survives the round-trip through
// /asset/$id (the page unmounts on navigation). Resets on full reload,
// which matches the "fresh visit starts at All" expectation.
let rememberedFilter: LibraryFilter = { mode: 'all' };

export function LibraryPage() {
  const queryClient = useQueryClient();
  const [filter, setFilterState] = useState<LibraryFilter>(rememberedFilter);
  const [selectMode, setSelectMode] = useState(false);
  const [slideshow, setSlideshow] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [downloading, setDownloading] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [bulkAction, setBulkAction] = useState<{
    kind: 'trash' | 'restore';
    done: number;
    total: number;
  } | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const server = useMemo(() => toServerFilter(filter), [filter]);
  const cacheFilter = useMemo(() => toCacheKey(filter), [filter]);

  // Switching filters reshuffles which assets are loaded, so clear any
  // selection to keep the count == what's actually downloadable.
  const setFilter = useCallback((next: LibraryFilter) => {
    rememberedFilter = next;
    setFilterState(next);
    setSelectedIds(new Set());
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const exitSelect = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | null }) =>
      api.get<AssetListResponse>('/v1/assets', {
        cursor: pageParam ?? undefined,
        limit: 100,
        include: server.include,
        kind: server.kind,
        capture_after: server.capture_after,
        capture_before: server.capture_before,
      }),
    [server],
  );

  const query = useInfiniteQuery({
    queryKey: qk.assets(cacheFilter),
    initialPageParam: null as string | null,
    queryFn,
    getNextPageParam: (page) => page.next_cursor,
  });

  // Register this list as the lightbox's neighbor source so /asset/{id}
  // can page forward past the loaded window. Favorites is filtered
  // client-side, so the lightbox must narrow the same way to keep its
  // prev/next order matching what's on screen.
  useEffect(() => {
    setLightboxSource({
      queryKey: qk.assets(cacheFilter),
      initialPageParam: null,
      queryFn,
      getNextPageParam: (page) => page.next_cursor,
      clientFilter: filter.mode === 'favorites' ? (a) => a.favorite : undefined,
    });
  }, [cacheFilter, queryFn, filter.mode]);

  const flatAssets = useMemo(() => {
    const pages = query.data?.pages ?? [];
    const out = pages.flatMap((p) => p.assets);
    if (filter.mode === 'favorites') return out.filter((a) => a.favorite);
    return out;
  }, [query.data, filter.mode]);

  const selectedAssets = useMemo(
    () => flatAssets.filter((a) => selectedIds.has(a.id)),
    [flatAssets, selectedIds],
  );

  const allSelected =
    flatAssets.length > 0 && selectedAssets.length === flatAssets.length;

  const handleDownload = useCallback(async () => {
    if (selectedAssets.length === 0 || downloading) return;
    setDownloading({ done: 0, total: selectedAssets.length });
    try {
      const zipName = `dvphoto-${selectedAssets.length}-items.zip`;
      await downloadAssetsZip(selectedAssets, zipName, (done, total) =>
        setDownloading({ done, total }),
      );
    } finally {
      setDownloading(null);
    }
  }, [selectedAssets, downloading]);

  const bulkTrashMutation = useMutation<void, ApiError, string[]>({
    mutationFn: async (ids) => {
      setBulkAction({ kind: 'trash', done: 0, total: ids.length });
      for (let i = 0; i < ids.length; i++) {
        await api.delete<void>(`/v1/assets/${ids[i]}`);
        setBulkAction({ kind: 'trash', done: i + 1, total: ids.length });
      }
    },
    onSuccess: () => {
      setSelectedIds(new Set());
      setSelectMode(false);
      queryClient.invalidateQueries({ queryKey: qk.assets() });
    },
    onError: (err) => setBulkError(err.reason),
    onSettled: () => setBulkAction(null),
  });

  const bulkRestoreMutation = useMutation<void, ApiError, string[]>({
    mutationFn: async (ids) => {
      setBulkAction({ kind: 'restore', done: 0, total: ids.length });
      for (let i = 0; i < ids.length; i++) {
        await api.post<void>(`/v1/assets/${ids[i]}/restore`, {});
        setBulkAction({ kind: 'restore', done: i + 1, total: ids.length });
      }
    },
    onSuccess: () => {
      setSelectedIds(new Set());
      setSelectMode(false);
      queryClient.invalidateQueries({ queryKey: qk.assets() });
    },
    onError: (err) => setBulkError(err.reason),
    onSettled: () => setBulkAction(null),
  });

  const handleBulkTrash = useCallback(() => {
    if (selectedAssets.length === 0 || bulkAction) return;
    const noun = selectedAssets.length === 1 ? 'photo' : 'photos';
    if (!window.confirm(`Move ${selectedAssets.length} ${noun} to trash?`)) return;
    setBulkError(null);
    bulkTrashMutation.mutate(selectedAssets.map((a) => a.id));
  }, [selectedAssets, bulkAction, bulkTrashMutation]);

  const handleBulkRestore = useCallback(() => {
    if (selectedAssets.length === 0 || bulkAction) return;
    setBulkError(null);
    bulkRestoreMutation.mutate(selectedAssets.map((a) => a.id));
  }, [selectedAssets, bulkAction, bulkRestoreMutation]);

  const toolButton =
    'rounded-full border border-white/10 px-3 py-1 text-xs font-medium text-neutral-300 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div className="flex h-full flex-col bg-neutral-950">
      {selectMode ? (
        <SelectionToolbar
          count={selectedAssets.length}
          allSelected={allSelected}
          downloading={downloading}
          bulkAction={bulkAction}
          bulkError={bulkError}
          trashMode={filter.mode === 'trash'}
          onSelectAll={() =>
            setSelectedIds(new Set(flatAssets.map((a) => a.id)))
          }
          onClear={() => setSelectedIds(new Set())}
          onDownload={() => void handleDownload()}
          onTrash={handleBulkTrash}
          onRestore={handleBulkRestore}
          onExit={exitSelect}
        />
      ) : (
        <FilterBar
          value={filter}
          onChange={setFilter}
          rightSlot={
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setSlideshow(true)}
                disabled={flatAssets.length === 0}
                className={toolButton}
              >
                Slideshow
              </button>
              <button
                type="button"
                onClick={() => setSelectMode(true)}
                className={toolButton}
              >
                Select
              </button>
            </div>
          }
        />
      )}
      <div className="flex-1 overflow-hidden">
        {query.isError ? (
          <div className="flex h-full items-center justify-center text-sm text-red-400">
            Failed to load: {(query.error as Error).message}
          </div>
        ) : query.isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-neutral-500">
            Loading…
          </div>
        ) : (
          <TimelineGrid
            assets={flatAssets}
            hasNextPage={query.hasNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            fetchNextPage={() => void query.fetchNextPage()}
            selectable={selectMode}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            scrollKey={scrollKeyFor(filter)}
            emptyLabel={
              filter.mode === 'favorites'
                ? 'No favorites in the loaded pages.'
                : filter.mode === 'trash'
                  ? 'Trash is empty.'
                  : filter.mode === 'video'
                    ? 'No videos yet.'
                    : filter.mode === 'photos'
                      ? 'No photos yet.'
                      : filter.mode === 'date'
                        ? 'Nothing captured in this month.'
                        : 'No photos yet.'
            }
          />
        )}
      </div>
      {slideshow && flatAssets.length > 0 && (
        <Slideshow
          assets={flatAssets}
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          fetchNextPage={() => void query.fetchNextPage()}
          onClose={() => setSlideshow(false)}
        />
      )}
    </div>
  );
}

// ────────────────────── Selection toolbar ──────────────────────

function SelectionToolbar({
  count,
  allSelected,
  downloading,
  bulkAction,
  bulkError,
  trashMode,
  onSelectAll,
  onClear,
  onDownload,
  onTrash,
  onRestore,
  onExit,
}: {
  count: number;
  allSelected: boolean;
  downloading: { done: number; total: number } | null;
  bulkAction: { kind: 'trash' | 'restore'; done: number; total: number } | null;
  bulkError: string | null;
  trashMode: boolean;
  onSelectAll: () => void;
  onClear: () => void;
  onDownload: () => void;
  onTrash: () => void;
  onRestore: () => void;
  onExit: () => void;
}) {
  const busy = downloading !== null || bulkAction !== null;
  const bulkLabel =
    bulkAction?.kind === 'trash'
      ? `Moving to trash ${bulkAction.done}/${bulkAction.total}…`
      : bulkAction?.kind === 'restore'
        ? `Restoring ${bulkAction.done}/${bulkAction.total}…`
        : null;
  return (
    <div className="flex min-h-12 shrink-0 flex-wrap items-center gap-3 border-b border-white/5 bg-neutral-950/95 px-4 py-2 backdrop-blur">
      <button
        type="button"
        onClick={onExit}
        aria-label="Exit selection"
        className="flex size-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-white/10 hover:text-white"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <span className="text-sm font-medium text-neutral-100">
        {count > 0 ? `${count} selected` : 'Select items'}
      </span>

      <button
        type="button"
        onClick={allSelected ? onClear : onSelectAll}
        className="rounded-full border border-white/10 px-2.5 py-1 text-xs text-neutral-300 transition-colors hover:bg-white/10 hover:text-white"
      >
        {allSelected ? 'Clear' : 'Select all loaded'}
      </button>

      <div className="ml-auto flex items-center gap-3">
        {(downloading || bulkAction) && (
          <span className="text-xs text-neutral-400">
            {downloading
              ? `Preparing zip ${downloading.done}/${downloading.total}…`
              : bulkLabel}
          </span>
        )}
        {bulkError && <span className="text-xs text-red-300">{bulkError}</span>}
        <button
          type="button"
          onClick={onDownload}
          disabled={count === 0 || busy}
          className="flex items-center gap-2 rounded-full border border-white/10 px-3 py-1 text-xs font-medium text-neutral-200 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {downloading ? 'Downloading…' : `Download${count > 0 ? ` (${count})` : ''}`}
        </button>
        {trashMode ? (
          <button
            type="button"
            onClick={onRestore}
            disabled={count === 0 || busy}
            className="flex items-center gap-2 rounded-full border border-emerald-900/70 bg-emerald-950/40 px-3 py-1 text-xs font-medium text-emerald-100 transition-colors hover:bg-emerald-900/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 12a9 9 0 0 1 15.5-6.2" />
              <path d="M21 12a9 9 0 0 1-15.5 6.2" />
              <path d="M18 2v4h-4" />
              <path d="M6 22v-4h4" />
            </svg>
            {bulkAction?.kind === 'restore' ? 'Restoring…' : `Restore${count > 0 ? ` (${count})` : ''}`}
          </button>
        ) : (
          <button
            type="button"
            onClick={onTrash}
            disabled={count === 0 || busy}
            className="flex items-center gap-2 rounded-full border border-red-900/70 bg-red-950/40 px-3 py-1 text-xs font-medium text-red-100 transition-colors hover:bg-red-900/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 6h18" />
              <path d="M8 6V4h8v2" />
              <path d="M19 6l-1 14H6L5 6" />
              <path d="M10 11v5" />
              <path d="M14 11v5" />
            </svg>
            {bulkAction?.kind === 'trash' ? 'Moving…' : `Move to trash${count > 0 ? ` (${count})` : ''}`}
          </button>
        )}
      </div>
    </div>
  );
}
